import { useRef, useState } from "react";
import { apiRequest } from "../../services/api";
export default function SalesVoidPreviewPanel({
  companyId,
  saleId,
  accessToken,
  canExecute = false,
  onVoided,
}) {
  const [date, setDate] = useState(""),
    [reason, setReason] = useState(""),
    [preview, setPreview] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [cashConfirmed, setCashConfirmed] = useState(false),
    [stockConfirmed, setStockConfirmed] = useState(false),
    [completed, setCompleted] = useState(null);
  const generation = useRef(0);
  const requestKey = useRef(null);
  function change(setter, value) {
    generation.current++;
    setter(value);
    setPreview(null);
    setError("");
    setCashConfirmed(false);
    setStockConfirmed(false);
    requestKey.current = null;
  }
  async function review(e) {
    e.preventDefault();
    const g = ++generation.current;
    setBusy(true);
    setError("");
    setPreview(null);
    setCashConfirmed(false);
    setStockConfirmed(false);
    requestKey.current = null;
    try {
      const r = await apiRequest(
        `/accounting/${companyId}/sales-corrections/pos/${saleId}/void-preview`,
        { accessToken, method: "POST", body: { date, reason } },
      );
      if (g === generation.current) setPreview(r);
    } catch (e) {
      if (g === generation.current) setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function execute() {
    setBusy(true);
    setError("");
    try {
      if (!requestKey.current) {
        if (!window.crypto?.randomUUID)
          throw Error("A secure HTTPS or localhost connection is required.");
        requestKey.current = window.crypto.randomUUID();
      }
      const r = await apiRequest(
        `/accounting/${companyId}/sales-corrections/pos/${saleId}/void`,
        {
          accessToken,
          method: "POST",
          body: {
            date,
            reason,
            previewHash: preview.previewHash,
            requestKey: requestKey.current,
            cashReturnedConfirmed: cashConfirmed,
            stockReceivedConfirmed: stockConfirmed,
          },
        },
      );
      setCompleted(r);
      onVoided?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  if (completed)
    return (
      <section className="mt-4 rounded-xl border border-emerald-200 bg-white p-4">
        <h3 className="font-semibold">Cash sale fully voided</h3>
        <p className="mt-2 text-sm">
          Reversal voucher {completed.voucher.number}. Original stock restored
          and session cash reduced once. Original receipt and accounting history
          retained.
        </p>
      </section>
    );
  return (
    <section className="mt-4 space-y-4 rounded-xl border border-amber-200 bg-white p-4">
      <h3 className="font-semibold text-slate-900">
        Full-void correction preview
      </h3>
      <p className="text-sm text-slate-600">
        Preview reversing entries, original-lot restoration and cash return
        amounts first. The separate confirmed action is limited to eligible
        same-day cash sales in your original open session. Partial returns and
        non-cash corrections remain unavailable.
      </p>
      <form onSubmit={review} className="space-y-3">
        <label className="block text-sm font-medium">
          Proposed correction date (India)
          <input
            type="date"
            required
            value={date}
            disabled={busy}
            onChange={(e) => change(setDate, e.target.value)}
            className="mt-1 block rounded-lg border p-2"
          />
        </label>
        <label className="block text-sm font-medium">
          Correction reason
          <textarea
            required
            minLength={20}
            maxLength={1000}
            value={reason}
            disabled={busy}
            onChange={(e) => change(setReason, e.target.value)}
            className="mt-1 w-full rounded-lg border p-3"
          />
        </label>
        <button
          type="submit"
          disabled={busy || !date || reason.trim().length < 20}
          className="rounded-lg border px-4 py-2 text-sm font-semibold disabled:opacity-40"
        >
          {busy ? "Checking…" : "Preview full void (no changes)"}
        </button>
      </form>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      {preview && (
        <div className="space-y-4 text-sm">
          <p className="rounded-lg bg-amber-50 p-3 font-medium">
            {preview.executionEnabled
              ? "Preview only — no changes have been made."
              : "Execution disabled — no changes have been made."}
          </p>
          <p>
            Original voucher: {preview.payload.originalVoucherNumber} · Proposed
            date: {preview.payload.date}
          </p>
          <div>
            <h4 className="font-semibold">Review blockers</h4>
            {preview.payload.eligibilityBlockers.length ? (
              <ul className="mt-2 list-disc pl-5">
                {preview.payload.eligibilityBlockers.map((b) => (
                  <li key={b}>{b.replaceAll("_", " ")}</li>
                ))}
              </ul>
            ) : (
              <p>
                No eligibility blockers found. Confirm the physical stock and
                actual cash return before executing.
              </p>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <caption className="text-left font-semibold">
                Planned reversing voucher — not posted
              </caption>
              <thead>
                <tr>
                  <th>Ledger</th>
                  <th>Debit ₹</th>
                  <th>Credit ₹</th>
                </tr>
              </thead>
              <tbody>
                {preview.payload.reversingLines.map((l, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-2">
                      {preview.ledgers.find((x) => x.id === l.ledgerId)?.name ||
                        l.ledgerId}
                    </td>
                    <td>{l.debit}</td>
                    <td>{l.credit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <h4 className="font-semibold">Planned refunds — not sent</h4>
            {preview.payload.refunds.map((r) => (
              <p key={r.method} className="mt-1">
                {r.method}: ₹{r.amount}
                {r.externalReversalEvidenceRequired
                  ? " · External refund verification required"
                  : ""}
              </p>
            ))}
            <p className="mt-2">
              Expected cash decrease: ₹{preview.payload.expectedCashDecrease}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <caption className="text-left font-semibold">
                Original stock restoration plan — not restored
              </caption>
              <thead>
                <tr>
                  <th>Lot</th>
                  <th>Quantity</th>
                  <th>Original unit cost ₹</th>
                </tr>
              </thead>
              <tbody>
                {preview.payload.restoration.map((r) => (
                  <tr key={r.allocationId} className="border-t">
                    <td className="py-2">{r.lotNumber}</td>
                    <td>{r.quantity}</td>
                    <td>{r.originalUnitCost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.executionEnabled && canExecute && (
            <div className="space-y-3 rounded-xl border border-red-200 p-4">
              <p className="font-semibold">Confirm full cash void</p>
              <p>
                This records a cash return you have physically made; it does not
                send money electronically. The receipt, stock restoration, till
                cash and reversing voucher commit together.
              </p>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  disabled={busy}
                  checked={cashConfirmed}
                  onChange={(e) => setCashConfirmed(e.target.checked)}
                />
                I have returned the full cash amount shown to the customer.
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  disabled={busy}
                  checked={stockConfirmed}
                  onChange={(e) => setStockConfirmed(e.target.checked)}
                />
                I have received and verified all original goods for restocking.
              </label>
              <button
                type="button"
                disabled={busy || !cashConfirmed || !stockConfirmed}
                onClick={execute}
                className="rounded-lg bg-red-700 px-4 py-3 font-semibold text-white disabled:opacity-40"
              >
                {busy ? "Voiding…" : "Confirm full cash void"}
              </button>
            </div>
          )}
          {preview.executionEnabled && !canExecute && (
            <p className="text-slate-600">
              Execution requires both accounting.vouchers_post and pos.void_sale
              permissions.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
